import React, { useCallback, useEffect } from 'react';

import { useViewModel } from './viewModel';

import styles from './sequence-viewer.module.css';

/**
 * Pill component for sequence slots.
 * @param {object} props - The component props.
 * @param {object} props.slot - The slot object.
 * @param {string} props.label - The pill label.
 * @param {function} props.onRemove - Remove handler.
 * @param {string} props.className - Additional className for styling the pill.
 * @param {boolean} props.isActive - Whether the pill is currently active.
 * @param {function} props.onSlotSelect - Handler for when the slot is selected for editing.
 */
function SequencePill({ slot, label, onRemove, className, isActive, onSlotSelect }) {
  const handleRemove = useCallback(
    (e) => {
      e.stopPropagation();
      onRemove(slot.id);
    },
    [onRemove, slot.id]
  );

  const handleSlotSelect = useCallback(
    (e) => {
      e.preventDefault();
      onSlotSelect(slot.id);
    },
    [onSlotSelect, slot.id]
  );

  return (
    <li className={styles['sequence-viewer__item']}>
      <button
        type="button"
        className={className}
        tabIndex={0}
        aria-current={isActive ? 'true' : undefined}
        aria-label={isActive ? `${label} (active)` : `${label} - click to edit`}
        onClick={handleSlotSelect}
      >
        {label}
        <span
          className={styles['sequence-viewer__remove']}
          role="button"
          aria-label="Remove charm"
          onClick={handleRemove}
        >
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M5.71677 1.71677C5.87298 1.56056 6.12696 1.56056 6.28317 1.71677C6.43938 1.87298 6.43938 2.12696 6.28317 2.28317L4.56638 3.99997L6.28317 5.71677L6.33395 5.78024C6.43632 5.93548 6.4198 6.14655 6.28317 6.28317C6.14655 6.4198 5.93548 6.43632 5.78024 6.33395L5.71677 6.28317L3.99997 4.56638L2.28317 6.28317C2.12696 6.43938 1.87298 6.43938 1.71677 6.28317C1.56056 6.12696 1.56056 5.87298 1.71677 5.71677L3.43356 3.99997L1.71677 2.28317L1.66599 2.2197C1.56362 2.06445 1.58014 1.85339 1.71677 1.71677C1.85339 1.58014 2.06445 1.56362 2.2197 1.66599L2.28317 1.71677L3.99997 3.43356L5.71677 1.71677Z"
              fill="white"
            />
          </svg>
        </span>
      </button>
    </li>
  );
}

/**
 * SequenceViewer component.
 * @param {object} props - Component props.
 * @param {function} props.setCurrentSlotId - Setter for current slot ID.
 * @param {string} props.currentSlotId - Current slot ID.
 * @param {function} [props.onOrderChange] - Handler for when the order changes.
 * @param {function} [props.onAdvancingChange] - Handler for when advancing state changes.
 */
export const SequenceViewer = ({ setCurrentSlotId, currentSlotId, onAdvancingChange }) => {
  const {
    variants,
    visibleSlots,
    showAddButton,
    handleAddSlot,
    handleRemove,
    sequentialSlotsTotal,
    sequentialViewerListRef,
    isAdvancing,
    populatedCount,
    handleSlotSelect
  } = useViewModel({ setCurrentSlotId, currentSlotId });

  // Notify parent component when advancing state changes
  useEffect(() => {
    if (onAdvancingChange) {
      onAdvancingChange(isAdvancing);
    }
  }, [isAdvancing, onAdvancingChange]);

  return (
    <div className={styles['sequence-viewer']}>
      <div className={styles['sequence-viewer__title'] + ' body-sm'}>
        Your charms: {populatedCount}/{sequentialSlotsTotal} selected
      </div>
      <ul ref={sequentialViewerListRef} className={styles['sequence-viewer__list']}>
        {visibleSlots.map((slot) => {
          const variant = slot.selected ? variants.get(slot.selected) : null;
          const label = variant
            ? variant.title === 'Default Title' && variant.product.title
              ? variant.product.title
              : variant.title
            : 'Select';
          const isActive = slot.id === currentSlotId;
          return (
            <SequencePill
              key={slot.id}
              slot={slot}
              label={label}
              onRemove={handleRemove}
              className={
                styles['sequence-viewer__pill'] +
                ' ' +
                (isActive ? styles['sequence-viewer__pill--active'] : '') +
                ' body-xs body-uppercase font-medium'
              }
              isActive={isActive}
              onSlotSelect={handleSlotSelect}
            />
          );
        })}
        {/* Show add button only if more slots can be added */}
        {showAddButton && (
          <li className={styles['sequence-viewer__item']}>
            <button
              type="button"
              className={styles['sequence-viewer__add'] + ' button-reset'}
              aria-label="Add charm"
              onClick={handleAddSlot}
            >
              <span className={styles['sequence-viewer__add-icon'] + ' svg-wrapper'}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.00001 1.7002C6.1657 1.7002 6.30001 1.83451 6.30001 2.0002V5.7002H10C10.1657 5.7002 10.3 5.83451 10.3 6.0002C10.3 6.16588 10.1657 6.3002 10 6.3002H6.30001V10.0002C6.30001 10.1659 6.1657 10.3002 6.00001 10.3002C5.83433 10.3002 5.70001 10.1659 5.70001 10.0002V6.3002H2.00001C1.83433 6.3002 1.70001 6.16588 1.70001 6.0002C1.70001 5.83451 1.83433 5.7002 2.00001 5.7002H5.70001V2.0002C5.70001 1.83451 5.83433 1.7002 6.00001 1.7002Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
            </button>
          </li>
        )}
      </ul>
    </div>
  );
};
